import { useRef, useEffect } from "react";
import type { ActionItem as ActionItemType, EditingActionItem } from "./types";

interface ActionItemProps {
  actionItem: ActionItemType;
  actionIndex: number;
  projectId: number; // -1 for global action items
  onEdit: (projectId: number, actionIndex: number, fieldIndex: number) => void;
  onStatusToggle?: (
    projectId: number,
    actionIndex: number,
    newStatus: "actionable" | "done" | "not actionable",
  ) => void;
  isEditing: boolean;
  editData?: EditingActionItem;
  onFieldChange: (
    field: keyof EditingActionItem,
    value: string | number,
  ) => void;
  onSave: () => void;
  onCancel: () => void;
  isFocused?: boolean;
  className?: string;
}

export default function ActionItem({
  actionItem,
  actionIndex,
  projectId,
  onEdit,
  onStatusToggle,
  isEditing,
  editData,
  onFieldChange,
  onSave,
  onCancel,
  isFocused = false,
  className = "",
}: ActionItemProps) {
  const contentRef = useRef<HTMLInputElement>(null);
  const statusRef = useRef<HTMLSelectElement>(null);
  const dateRef = useRef<HTMLInputElement>(null);
  const delegatedRef = useRef<HTMLInputElement>(null);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      onSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      onCancel();
    } else if (e.key === "Tab" && editData) {
      e.preventDefault();
      handleTabNavigation();
    }
  };

  const handleTabNavigation = () => {
    if (!editData) return;

    const refs = [contentRef, statusRef, dateRef, delegatedRef];
    const nextFieldIndex = (editData.fieldIndex + 1) % refs.length;

    // Update the field index in the parent
    onFieldChange("fieldIndex", nextFieldIndex);

    // Focus the next field
    setTimeout(() => {
      refs[nextFieldIndex]?.current?.focus();
    }, 0);
  };

  // Auto-focus the correct field when entering edit mode
  useEffect(() => {
    if (isEditing && editData) {
      const refs = [contentRef, statusRef, dateRef, delegatedRef];
      const targetRef = refs[editData.fieldIndex] || contentRef;
      setTimeout(() => targetRef.current?.focus(), 0);
    }
  }, [isEditing, editData]);

  const handleStatusToggle = () => {
    if (!onStatusToggle) return;

    if (actionItem.status === "actionable") {
      onStatusToggle(projectId, actionIndex, "done");
    } else if (actionItem.status === "not actionable") {
      onStatusToggle(projectId, actionIndex, "actionable");
    }
  };

  const renderStatusButton = () => {
    if (actionItem.status === "done") {
      return null; // No button for done items
    }

    if (actionItem.status === "actionable") {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle();
          }}
          className="mr-3 p-1 rounded-full bg-green-100 hover:bg-green-200 text-green-700 transition-colors"
          title="Mark as done"
          aria-label="Mark as done"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        </button>
      );
    }

    if (actionItem.status === "not actionable") {
      return (
        <button
          onClick={(e) => {
            e.stopPropagation();
            handleStatusToggle();
          }}
          className="mr-3 p-1 rounded-full bg-yellow-100 hover:bg-yellow-200 text-yellow-700 transition-colors"
          title="Mark as actionable"
          aria-label="Mark as actionable"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 10V3L4 14h7v7l9-11h-7z"
            />
          </svg>
        </button>
      );
    }

    return null;
  };

  if (isEditing && editData) {
    return (
      <div className={`p-3 rounded ${className}`}>
        <div className="space-y-2">
          <input
            ref={contentRef}
            type="text"
            placeholder="Action item content"
            value={editData.content}
            onChange={(e) => onFieldChange("content", e.target.value)}
            onKeyDown={handleKeyDown}
            className="w-full p-2 border border-green-500 rounded"
          />
          <div className="flex gap-2">
            <select
              ref={statusRef}
              value={editData.status}
              onChange={(e) => onFieldChange("status", e.target.value)}
              className="p-2 border border-green-500 rounded"
            >
              <option value="actionable">Actionable</option>
              <option value="done">Done</option>
              <option value="not actionable">Not Actionable</option>
            </select>
            <input
              ref={dateRef}
              type="date"
              value={editData.date}
              onChange={(e) => onFieldChange("date", e.target.value)}
              className="p-2 border border-green-500 rounded"
            />
            <input
              ref={delegatedRef}
              type="text"
              placeholder="Delegated to"
              value={editData.delegatedTo}
              onChange={(e) => onFieldChange("delegatedTo", e.target.value)}
              className="flex-1 p-2 border border-green-500 rounded"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`p-3 rounded focus:outline-none ${
        isFocused
          ? "ring-2 ring-green-300 bg-green-50"
          : "bg-white border border-gray-200 hover:border-gray-300"
      } ${className}`}
      tabIndex={0}
      data-action-item-container={projectId !== -1 ? "" : undefined}
      data-global-action-item-container={projectId === -1 ? "" : undefined}
      data-action-item-index={actionIndex}
      data-project-id={projectId}
    >
      <div className="flex items-center justify-between">
        {renderStatusButton()}
        <div className="flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`cursor-pointer hover:text-green-600 transition-colors ${
                actionItem.status === "done" ? "line-through text-gray-500" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(projectId, actionIndex, 0);
              }}
            >
              {actionItem.content}
            </span>
            <span
              className={`px-2 py-1 rounded text-xs cursor-pointer hover:opacity-75 transition-opacity ${
                actionItem.status === "actionable"
                  ? "bg-green-100 text-green-800"
                  : actionItem.status === "done"
                    ? "bg-blue-100 text-blue-800"
                    : "bg-gray-100 text-gray-800"
              }`}
              onClick={(e) => {
                e.stopPropagation();
                onEdit(projectId, actionIndex, 1);
              }}
            >
              {actionItem.status === "not actionable"
                ? "Not Actionable"
                : actionItem.status.charAt(0).toUpperCase() +
                  actionItem.status.slice(1)}
            </span>
          </div>
          <div className="text-xs text-gray-500 mt-1">
            {actionItem.date && (
              <span
                className="cursor-pointer hover:text-green-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(projectId, actionIndex, 2);
                }}
              >
                Due: {actionItem.date}
              </span>
            )}
            {actionItem.delegatedTo && (
              <span
                className="ml-2 cursor-pointer hover:text-green-600 transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(projectId, actionIndex, 3);
                }}
              >
                Delegated to: {actionItem.delegatedTo}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
